<template>
  <v-select
    :value="selectedValues"
    :items="items"
    :item-text="itemText"
    :item-value="itemValue"
    :label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    :menu-props="menuProps"
    :color="color"
    :outlined="outlined"
    :dense="dense"
    :hide-details="hideDetails"
    multiple
    chips
    v-bind="selectProps"
    @input="onSelectionChange"
  >
    <template v-slot:selection="slotData">
      <v-chip
        :key="getSelectionKey(slotData.item)"
        :disabled="disabled"
        outlined
        small
        class="my-select-chip"
        @click.stop
      >
        <span class="my-select-chip__label">
          {{ getSelectionLabel(slotData.item) }}
        </span>

        <v-text-field
          :value="customTexts[getSelectionKey(slotData.item)] || ''"
          :disabled="disabled"
          hide-details
          dense
          flat
          solo
          class="my-select-chip__input"
          placeholder="..."
          @input="onCustomTextInput(getSelectionKey(slotData.item), $event)"
          @click.native.stop
          @mousedown.native.stop
          @mouseup.native.stop
          @keydown.native.stop
        />

        <v-icon
          small
          class="my-select-chip__remove"
          @click.stop="removeSelection(slotData.item)"
        >
          mdi-close
        </v-icon>
      </v-chip>
    </template>
  </v-select>
</template>

<script>
export default {
  name: "MySelectChipsCustomText",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemText: {
      type: String,
      default: "text",
    },
    itemValue: {
      type: String,
      default: "value",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    menuProps: {
      type: [Object, String],
      default: () => ({ maxHeight: "400" }),
    },
    color: {
      type: String,
      default: "primary",
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [Boolean, String],
      default: "auto",
    },
    selectProps: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedValues: [],
      customTexts: {},
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.syncFromModel(newVal || []);
      },
    },
  },
  methods: {
    syncFromModel(modelArray) {
      const nextSelected = [];
      const nextCustomTexts = {};

      for (const entry of modelArray) {
        if (entry && typeof entry === "object" && Object.prototype.hasOwnProperty.call(entry, "key")) {
          nextSelected.push(entry.key);
          nextCustomTexts[entry.key] = entry.values || "";
        } else if (entry && typeof entry === "object" && Object.prototype.hasOwnProperty.call(entry, "value")) {
          nextSelected.push(entry.value);
          nextCustomTexts[entry.value] = entry.customText || "";
        } else {
          nextSelected.push(entry);
          nextCustomTexts[entry] = "";
        }
      }

      this.selectedValues = nextSelected;
      this.customTexts = nextCustomTexts;
    },
    onSelectionChange(newSelection) {
      this.selectedValues = Array.isArray(newSelection) ? newSelection : [];

      const selectedKeys = new Set(this.selectedValues.map((val) => String(val)));
      for (const key of Object.keys(this.customTexts)) {
        if (!selectedKeys.has(String(key))) {
          this.$delete(this.customTexts, key);
        }
      }

      for (const val of this.selectedValues) {
        if (!Object.prototype.hasOwnProperty.call(this.customTexts, val)) {
          this.$set(this.customTexts, val, "");
        }
      }

      this.emitModel();
    },
    onCustomTextInput(selectionValue, text) {
      this.$set(this.customTexts, selectionValue, text);
      this.emitModel();
    },
    removeSelection(selectionItem) {
      const selectionKey = this.getSelectionKey(selectionItem);
      this.selectedValues = this.selectedValues.filter((value) => String(value) !== String(selectionKey));
      if (Object.prototype.hasOwnProperty.call(this.customTexts, selectionKey)) {
        this.$delete(this.customTexts, selectionKey);
      }
      this.emitModel();
    },
    getSelectionKey(selectionItem) {
      if (selectionItem && typeof selectionItem === "object") {
        if (Object.prototype.hasOwnProperty.call(selectionItem, this.itemValue)) {
          return selectionItem[this.itemValue];
        }
        if (Object.prototype.hasOwnProperty.call(selectionItem, "value")) {
          return selectionItem.value;
        }
      }
      return selectionItem;
    },
    getSelectionLabel(selectionItem) {
      if (selectionItem && typeof selectionItem === "object") {
        if (Object.prototype.hasOwnProperty.call(selectionItem, this.itemText)) {
          return selectionItem[this.itemText];
        }
        if (Object.prototype.hasOwnProperty.call(selectionItem, "text")) {
          return selectionItem.text;
        }
      }

      const key = this.getSelectionKey(selectionItem);
      const foundItem = this.items.find((item) => {
        if (item && typeof item === "object") {
          return String(item[this.itemValue]) === String(key);
        }
        return String(item) === String(key);
      });

      if (foundItem && typeof foundItem === "object") {
        return foundItem[this.itemText];
      }
      return key;
    },
    emitModel() {
      const model = this.selectedValues.map((val) => ({
        key: val,
        values: this.customTexts[val] || "",
      }));
      this.$emit("input", model);
      this.$emit("change", model);
    },
  },
};
</script>

<style scoped>
.my-select-chip {
  max-width: 100%;
  min-height: 34px;
  border: 1px solid rgba(0, 0, 0, 0.23) !important;
}

.my-select-chip__label {
  margin-right: 8px;
  white-space: nowrap;
}

.my-select-chip__input {
  min-width: 90px;
  max-width: 180px;
}

.my-select-chip__input,
.my-select-chip__input ::v-deep .v-input,
.my-select-chip__input ::v-deep .v-input__control,
.my-select-chip__input ::v-deep .v-input__slot,
.my-select-chip__input ::v-deep .v-text-field__slot,
.my-select-chip__input ::v-deep input {
  user-select: text !important;
  pointer-events: auto !important;
}

.my-select-chip__input ::v-deep .v-input__slot {
  min-height: 30px !important;
  margin-bottom: 0 !important;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: 0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.32) !important;
  border-radius: 0 !important;
}

.my-select-chip__input ::v-deep input {
  font-size: 12px;
  line-height: 20px;
}

.my-select-chip__remove {
  margin-left: 6px;
  cursor: pointer;
}
</style>
